import { fetchUsersFromCompaniesApi } from "API/apis/admin/users/fetchUsers";
import { useGlobalContext } from "context/GlobalContext";
import { createContext, useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { message } from "constants/responses/message";
import { fetchModulesMasterApi } from "API/apis/master/getModules";
import { filterStringToObject } from "utils/functions/reactTableFilterConverter";

const initialModuleValue = {
  moduleName: "",
  moduleAccessCode: "",
  subModule: [{ subModuleName: "", subModuleAccessCode: "" }],
};
const UsersContext = createContext({
  isListUpdated: false,
  setIsListUpdated: () => {},
  list: [],
  setList: () => {},
  allModules: [],
  setAllModules: () => {},
  isReloadRequired: false,
  setIsReloadRequired: () => {},
});

export const UsersProvider = ({ children }) => {
  const [searchParams] = useSearchParams();
  const paramsToObject = Object.fromEntries(searchParams.entries());

  const [list, setList] = useState([]);
  const [allModules, setAllModules] = useState([initialModuleValue]);
  const [isReloadRequired, setIsReloadRequired] = useState(false);
  const { setIsInitializing, setError, setIsLoading, setSuccess } =
    useGlobalContext();

  const [allParams, setAllParams] = useState({
    ...paramsToObject,
    filters: filterStringToObject(paramsToObject.filters) || "",
  });

  useEffect(() => {
    setAllParams({
      ...paramsToObject,
      filters: filterStringToObject(paramsToObject.filters),
    });
  }, [searchParams]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsInitializing(true);

        const [companiesResponse, allModulesResponse] = await Promise.all([
          fetchUsersFromCompaniesApi(allParams),
          fetchModulesMasterApi(),
        ]);

        if (
          companiesResponse.status !== 200 &&
          allModulesResponse.status !== 200
        ) {
          throw new Error(message.error.generalFailure);
        }
        setAllModules(allModulesResponse.data);
        setList(companiesResponse.data);
        setIsInitializing(false);
      } catch (error) {
        setError(error.message);
      } finally {
        setIsInitializing(false);
        setIsLoading(false);
        setSuccess("");
        setError("");
      }
    };
    fetchData();
  }, [allParams, isReloadRequired]);

  return (
    <UsersContext.Provider
      value={{
        list,
        setList,
        allModules,
        setAllModules,
        isReloadRequired,
        setIsReloadRequired,
      }}
    >
      <>{children}</>
    </UsersContext.Provider>
  );
};

export const useUsersContext = () => {
  const context = useContext(UsersContext);
  if (!context) {
    throw new Error("useUsersContext must be used within a UsersProvider");
  }
  return context;
};
